import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import Select from 'core/components/Select';
import Tag from 'core/components/Tag';
import {
  useNotify,
} from 'core/hooks';
import {
  useUpdateAppointmentStatus,
} from 'pages/Dashboard/pages/Appointments/pages/List/hooks';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  useEncounterTypeStatuses,
} from 'views/EMR/stores/appointmentStatus';
import {
  AppointmentStatusItem,
} from 'views/EMR/stores/appointmentStatusHelpers';
import {
  lightenHexColor,
} from 'utils/colors';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import {
 pluralize,
} from '@populateui/utils';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import compact from 'lodash/compact';

type Part = 'root' | 'label' | 'select';

type Props = {
  classes?: Partial<Record<Part, string>>;
  value: string;
  encounterId?: number;
  disabled?: boolean;
  onUpdate?: () => Promise<void>;
  statusColor?: string;
  appointmentDate?: string | Date | Dayjs | null;
};

type StatusDisplayProps = {
  statusColor?: string;
  value: string;
};

function StatusDisplay({
  statusColor,
  value,
}: StatusDisplayProps) {
  return !isNil(statusColor) ? (
    <div className="flex items-center gap-2">
      <div
        className={clsx(statusColor, 'w-2.5 h-2.5 rounded-full')}
        style={{ backgroundColor: statusColor }}
      />
      <span
        className="!font-medium"
        style={{
          color: statusColor,
          WebkitTextFillColor: statusColor,
        }}
      >
        {value}
      </span>
    </div>
  ) : (
    <p>{value}</p>
  );
}

type StatusValueProps = {
  isUpdating: boolean;
  statusColor?: string;
  value: string;
};

function StatusValue({
  isUpdating,
  statusColor,
  value,
}: StatusValueProps) {
  return isUpdating ? (
    <p>
      <Spinner className="mr-2" />
      Updating...
    </p>
  ) : (
    <StatusDisplay statusColor={statusColor} value={value} />
  );
}

type OptionLabelProps = {
  option: AppointmentStatusItem;
  selectedStatus: AppointmentStatusItem;
  showOccupancyLabels: boolean;
};

function OptionLabel({
  option,
  selectedStatus,
  showOccupancyLabels,
}: OptionLabelProps) {
  const isCurrent = option.value === selectedStatus.value;

  return (
    <div className="flex items-center justify-between gap-2 w-full">
      <span>{option.value}</span>
      {showOccupancyLabels && option.occupiedCount > 0 && (
        isCurrent ? (
          <Tag
            tagName="Current"
            className="inline-flex items-center gap-1"
            defaultColor="!bg-emerald-100 !text-emerald-800 font-medium"
            tooltip={compact([
              'Patient in this room',
              option.occupiedCount > 1 ? `along with ${pluralize('patient', option.occupiedCount - 1)}` : undefined,
            ]).join(', ')}
          >
            <CheckCircleOutlineOutlinedIcon className="!text-sm" />
            Current
          </Tag>
        ) : (
          <Tag
            tagName="Busy"
            className="inline-flex items-center gap-1"
            defaultColor="!bg-amber-100 !text-amber-800 font-medium"
            tooltip={`Occupied by ${pluralize('patient', option.occupiedCount)}`}
          >
            <PeopleAltOutlinedIcon className="!text-sm" />
            Busy
          </Tag>
        )
      )}
    </div>
  );
}

export default function AppointmentStatusSelect({
  classes,
  value,
  encounterId,
  disabled = false,
  onUpdate,
  statusColor,
  appointmentDate,
}: Props) {
  const encounterTypeStatuses = useEncounterTypeStatuses();
  const [isUpdating, setIsUpdating] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const { updateAppointmentStatus } = useUpdateAppointmentStatus();
  const notify = useNotify();

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const selectedStatus = encounterTypeStatuses.find(
    (status) => status.value === localValue,
  ) ?? {
    value: localValue,
    roomSettingId: null,
    occupiedCount: 0,
  };

  const showOccupancyLabels = !isNil(appointmentDate)
    && dayjs(appointmentDate).isSame(dayjs(), 'day');

  const lightBorderColor = useMemo(
    () => (statusColor ? lightenHexColor(statusColor, 80) : undefined),
    [statusColor],
  );

  const handleAppointmentStatusChange = async (
    option: AppointmentStatusItem | AppointmentStatusItem[],
  ) => {
    const nextStatus = Array.isArray(option) ? option[0] : option;
    setIsUpdating(true);
    setLocalValue(nextStatus.value);
    try {
      await updateAppointmentStatus({
        encounterId,
        patientAppointmentStatus: nextStatus.value,
      });

      await onUpdate?.();
    } catch {
      setLocalValue(value);
      notify({
        message: 'Failed to update appointment status.',
        variant: 'error',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Select<AppointmentStatusItem>
      id="appointment-status"
      className={classes?.root}
      labelClasses={classes?.label}
      placeholder="Select"
      value={selectedStatus}
      options={encounterTypeStatuses}
      getLabel={(option) => (
        <OptionLabel
          option={option}
          selectedStatus={selectedStatus}
          showOccupancyLabels={showOccupancyLabels}
        />
      )}
      getValue={(option) => option.value}
      onChange={handleAppointmentStatusChange}
      disabled={disabled || isUpdating}
      muiClasses={{
        select: classes?.select,
      }}
      sx={lightBorderColor ? {
        border: `1px solid ${lightBorderColor} !important`,
      } : undefined}
      menuProps={{
        anchorOrigin: {
          horizontal: 'left',
          vertical: 'bottom',
        },
        transformOrigin: {
          horizontal: 'left',
          vertical: 'top',
        },
        slotProps: {
          paper: {
            sx: {
              minWidth: '280px !important',
            },
          },
        },
      }}
      allowInvalid
      renderValue={(val: string) => (
        <StatusValue
          isUpdating={isUpdating}
          statusColor={statusColor}
          value={val}
        />
      )}
    />
  );
}
